import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, Pencil } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import type { CoaAccount, CoaAccountType } from './CoaPreview';
import { trialBalanceTotals } from '../lib/coaBalance';
import CoaResultTree from './CoaResultTree';

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * One direct-import proposal from `POST /firms/coa-map`. The backend keeps the
 * imported code/description and conforms a final 5-digit code. There is no
 * template mapping (`action`/`target_code` were removed).
 */
export interface CoaImportProposal {
  imported_code: string;
  imported_name: string;
  account_code: string;
  account_name: string;
  account_type: CoaAccountType;
  parent_code: string | null;
  balance?: number | null;
  reason?: string;
  source: 'llm' | 'heuristic';
}

interface Props {
  proposals: CoaImportProposal[];
  onApply: (accounts: CoaAccount[], balances: Record<string, number>) => void;
  /** Called when the user opens the table again after applying (Edit mapping). */
  onEdit?: () => void;
  provider?: string | null;
  usage?: { prompt: number; completion: number; total: number } | null;
  /** When set, the review's rows/applied result are persisted under this scope. */
  draftKey?: string;
}

/** localStorage key for a page's review draft (rows + applied result). */
export function reviewDraftStorageKey(draftKey: string): string {
  return `coaMapReviewDraft:v1:${draftKey}`;
}

interface ReviewDraft {
  signature: string;
  rows: ReviewRow[];
  applied: { accounts: CoaAccount[]; balances: Record<string, number> } | null;
}

function readReviewDraft(storageKey: string | null, signature: string): ReviewDraft | null {
  if (!storageKey) return null;
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null') as ReviewDraft | null;
    if (saved && saved.signature === signature && Array.isArray(saved.rows)) return saved;
  } catch { /* ignore malformed draft */ }
  return null;
}

/** One editable review row (proposal + user edits). */
export interface ReviewRow {
  proposal: CoaImportProposal;
  included: boolean;
  /** Final account code (editable; defaults to `proposal.account_code`). */
  account_code: string;
  /** Final account name (editable; defaults to `proposal.account_name`). */
  account_name: string;
  account_type: CoaAccountType;
  parent_code: string | null;
  balance?: number;
  /** Validation error shown under the name input. */
  nameError?: string;
  /** Validation error shown under the code input. */
  codeError?: string;
  /** Validation error shown under the parent input. */
  parentError?: string;
  /** Original index of `proposal` in the `proposals` array — stable row identity. */
  proposalIndex?: number;
  /** Reason shown for a row the client-side guard flagged. */
  reason?: string;
}

/**
 * Recompute the codes/names/parents of every included row:
 * - name is empty → `Name required`; duplicated (case-insensitive) → `Duplicate name`
 * - code duplicated among included rows → `Duplicate code`
 * - `parent_code` not one of the included codes → `Parent not included`
 * Excluded rows never carry an error. Closure is guaranteed by the backend, so a
 * missing parent only arises when the user excludes an ancestor row.
 */
function applyErrors(rows: ReviewRow[]): ReviewRow[] {
  const nameCounts = new Map<string, number>();
  const codeCounts = new Map<string, number>();
  const includedCodes = new Set<string>();
  for (const r of rows) {
    if (!r.included) continue;
    const n = (r.account_name ?? '').trim().toLowerCase();
    nameCounts.set(n, (nameCounts.get(n) ?? 0) + 1);
    const c = (r.account_code ?? '').trim();
    if (c) {
      codeCounts.set(c, (codeCounts.get(c) ?? 0) + 1);
      includedCodes.add(c);
    }
  }
  return rows.map(r => {
    if (!r.included) {
      return (r.nameError || r.codeError || r.parentError)
        ? { ...r, nameError: undefined, codeError: undefined, parentError: undefined }
        : r;
    }
    const n = (r.account_name ?? '').trim().toLowerCase();
    const nameError = !n ? 'Name required' : (nameCounts.get(n) ?? 0) > 1 ? 'Duplicate name' : undefined;
    const c = (r.account_code ?? '').trim();
    const codeError = c && (codeCounts.get(c) ?? 0) > 1 ? 'Duplicate code' : undefined;
    const parentError = r.parent_code && !includedCodes.has(r.parent_code) ? 'Parent not included' : undefined;
    const changed = r.nameError !== nameError || r.codeError !== codeError || r.parentError !== parentError;
    return changed ? { ...r, nameError, codeError, parentError } : r;
  });
}

const TYPE_LABELS: Record<string, string> = {
  asset: tr('Assets', '資產', '资产'),
  liability: tr('Liabilities', '負債', '负债'),
  equity: tr('Equity', '權益', '权益'),
  revenue: tr('Revenue', '收入', '收入'),
  other_income: tr('Other Income', '其他收入', '其他收入'),
  cost: tr('Cost', '直接成本', '直接成本'),
  expense: tr('Expenses', '支出', '支出'),
  other_expense: tr('Other Expenses', '其他支出', '其他支出'),
};

const TYPE_ORDER: CoaAccountType[] = [
  'asset', 'liability', 'equity', 'revenue', 'other_income', 'cost', 'expense', 'other_expense',
];

const CLASS_ROOTS: Record<string, string> = {
  asset: '10000',
  liability: '20000',
  equity: '30000',
  revenue: '40000',
  cost: '50000',
  expense: '60000',
  other_income: '80000',
  other_expense: '90000',
};

// ── Pure helpers ───────────────────────────────────────────────────────────

/** Return a copy of `rows` ordered by final code (stable, code order). */
export function sortRowsByCode(rows: ReviewRow[]): ReviewRow[] {
  return [...rows].sort((a, b) => (a.account_code || '').localeCompare(b.account_code || ''));
}

/** Nearest parent header code above a 5-digit leaf (mirrors frontend/src/lib/coaImport.ts). */
export function roundDownParent(code: string): string | null {
  const c = String(code ?? '');
  if (c.endsWith('0000')) return null;
  if (c.endsWith('000')) return `${c[0]}0000`;
  if (c.endsWith('00')) return `${c.slice(0, 2)}000`;
  return `${c.slice(0, 3)}00`;
}

/**
 * Turn editable review rows into the final `accounts[]`/`balances` payload.
 * Direct import: each included row becomes its own account (`account_code`,
 * `account_name`, `account_type`, `parent_code`). Closure is guaranteed by the
 * backend, so no template ancestors are synthesised here.
 *
 * Balances keep the uploaded sign: positive figures are debits and negative
 * figures are credits. No account-type normalization is applied on import.
 */
export function buildAppliedAccounts(
  rows: ReviewRow[],
): { accounts: CoaAccount[]; balances: Record<string, number> } {
  const accounts: CoaAccount[] = [];
  const balances: Record<string, number> = {};
  const seen = new Set<string>();

  for (const row of rows) {
    if (!row.included) continue;
    const code = row.account_code;
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const account: CoaAccount = {
      account_code: code,
      account_name: row.account_name || row.proposal.account_name || row.proposal.imported_name,
      account_type: row.account_type,
      parent_code: row.parent_code ?? null,
    };
    accounts.push(account);
    if (row.balance !== undefined) {
      balances[code] = row.balance;
    }
  }

  accounts.sort((a, b) => a.account_code.localeCompare(b.account_code));
  return { accounts, balances };
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * Validate a manual leaf-code edit on the applied result: exactly 5 digits,
 * leading digit matches the account's COA category, unique among applied rows,
 * and the parent stays in the same category. Returns an error message or null.
 */
export function validateCoaCodeEdit(
  accounts: CoaAccount[],
  oldCode: string,
  newCode: string,
): string | null {
  if (!/^\d{5}$/.test(newCode)) {
    return tr('Code must be exactly 5 digits', '編號必須為5位數字', '编号必须为5位数字');
  }
  const account = accounts.find(a => a.account_code === oldCode);
  if (!account) return tr('Account not found', '找不到科目', '找不到科目');
  const classRoot = CLASS_ROOTS[account.account_type];
  if (classRoot && newCode[0] !== classRoot[0]) {
    return tr('Code must be under the same COA category', '編號必須屬於相同的科目類別', '编号必须属于相同的科目类别');
  }
  if (accounts.some(a => a.account_code === newCode && a.account_code !== oldCode)) {
    return tr('Code already in use', '編號已被使用', '编号已被使用');
  }
  const parent = roundDownParent(newCode);
  if (parent && newCode[0] !== parent[0]) {
    return tr('Parent must be under the same COA category', '上級科目必須屬於相同的科目類別', '上级科目必须属于相同的科目类别');
  }
  return null;
}

export default function CoaMapReview({ proposals, onApply, onEdit, provider, usage, draftKey }: Props) {
  const signature = useMemo(
    () => proposals.map(p => `${p.imported_code}:${p.account_code}`).join('|'),
    [proposals],
  );
  const storageKey = draftKey ? reviewDraftStorageKey(draftKey) : null;
  const [applied, setApplied] = useState<{ accounts: CoaAccount[]; balances: Record<string, number> } | null>(
    () => readReviewDraft(storageKey, signature)?.applied ?? null,
  );

  const [rows, setRows] = useState<ReviewRow[]>(() => {
    const restored = readReviewDraft(storageKey, signature);
    if (restored) return restored.rows;
    return applyErrors(sortRowsByCode(proposals.map((p, proposalIndex): ReviewRow => ({
      proposal: p,
      included: true,
      account_code: p.account_code,
      account_name: p.account_name,
      account_type: p.account_type,
      parent_code: p.parent_code ?? null,
      proposalIndex,
      ...(p.balance != null ? { balance: p.balance } : {}),
    }))));
  });

  // Persist the editable review (rows + applied result) so a refresh restores it.
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ signature, rows, applied }));
    } catch { /* ignore quota errors */ }
  }, [storageKey, signature, rows, applied]);

  const patch = (idx: number, changes: Partial<ReviewRow>) =>
    setRows(prev => applyErrors(prev.map((r, i) => (i === idx ? { ...r, ...changes } : r))));

  const toggleInclude = (idx: number) =>
    setRows(prev => applyErrors(prev.map((r, i) => (i === idx ? { ...r, included: !r.included } : r))));

  const handleTypeChange = (idx: number, type: CoaAccountType) => patch(idx, { account_type: type });

  const handleBalanceChange = (idx: number, value: string) => {
    if (value === '') {
      setRows(prev => prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r };
        delete next.balance;
        return next;
      }));
    } else {
      patch(idx, { balance: parseFloat(value) });
    }
  };

  const handleApply = () => {
    const { accounts, balances } = buildAppliedAccounts(rows);
    onApply(accounts, balances);
    // An empty payload means every row was excluded: the parent keeps the review
    // open and shows its own error, so do not swap to an empty result tree.
    if (accounts.length > 0) setApplied({ accounts, balances });
  };

  /** Rename an applied account (post-Apply tree). */
  const applyAppliedName = (code: string, name: string) => {
    if (!applied) return;
    const accounts = applied.accounts.map(a =>
      a.account_code === code ? { ...a, account_name: name } : a,
    );
    setApplied({ accounts, balances: applied.balances });
    onApply(accounts, applied.balances);
  };

  /** Edit an applied leaf code, re-sorting by code. Returns an error, or null. */
  const applyAppliedCode = (oldCode: string, newCode: string): string | null => {
    if (!applied) return null;
    const err = validateCoaCodeEdit(applied.accounts, oldCode, newCode);
    if (err) return err;
    const accounts = applied.accounts
      .map(a => ({
        ...a,
        account_code: a.account_code === oldCode ? newCode : a.account_code,
        parent_code: a.parent_code === oldCode ? newCode : a.parent_code,
      }))
      .sort((a, b) => a.account_code.localeCompare(b.account_code));
    const balances: Record<string, number> = {};
    for (const [k, v] of Object.entries(applied.balances)) balances[k === oldCode ? newCode : k] = v;
    setApplied({ accounts, balances });
    onApply(accounts, balances);
    return null;
  };

  const includedRows = rows.filter(r => r.included);
  const tb = trialBalanceTotals(
    includedRows
      .filter(r => r.balance !== undefined)
      .map(r => ({ balance: r.balance as number, account_type: r.account_type })),
  );
  const imbalanced = tb.differenceCents !== 0;
  const tbWarning = imbalanced && (
    <div data-testid="coa-map-tb-warning" className="flex items-start gap-1 text-amber-900 dark:text-amber-100 text-xs">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span>
        {tr(
          `Trial balance is out by ${Math.abs(tb.differenceCents) / 100} (you can still apply)`,
          `試算表相差 ${Math.abs(tb.differenceCents) / 100}（仍可套用）`,
          `试算表相差 ${Math.abs(tb.differenceCents) / 100}（仍可应用）`,
        )}
      </span>
    </div>
  );

  return (
    <div data-testid="coa-map-review" className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">
          {tr('Review Import', '審閱導入', '审阅导入')}
        </label>
        <div className="flex items-center gap-3">
          {provider || usage ? (
            <span data-testid="coa-map-usage" className="text-xs text-muted-foreground">
              {tr('LLM import', 'LLM 導入', 'LLM 导入')}{provider ? ` · ${provider}` : ''}
              {usage ? ` · ${usage.total.toLocaleString('en-US')} ${tr('tokens', '詞元', '词元')} (prompt ${usage.prompt.toLocaleString('en-US')} / completion ${usage.completion.toLocaleString('en-US')})` : ''}
            </span>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {tr(`${includedRows.length} of ${rows.length} rows`, `${includedRows.length} / ${rows.length} 行`, `${includedRows.length} / ${rows.length} 行`)}
          </span>
        </div>
      </div>

      {applied ? (
        <>
          <CoaResultTree
            accounts={applied.accounts}
            balances={applied.balances}
            onEditName={applyAppliedName}
            onEditCode={applyAppliedCode}
          />
          {tbWarning}
          <div className="flex justify-end pt-1">
            <button
              data-testid="coa-map-edit"
              onClick={() => { setApplied(null); onEdit?.(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded text-xs font-medium hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5" />
              {tr('Edit import', '編輯導入', '编辑导入')}
            </button>
          </div>
        </>
      ) : (
      <>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/30">
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">{tr('# Imported', '# 導入', '# 导入')}</th>
              <th className="px-2 py-1.5 font-medium">{tr('Type', '類型', '类型')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{tr('Balance b/f', '承前結餘', '承前结余')}</th>
              <th className="px-2 py-1.5 font-medium text-center">{tr('Include', '包括', '包括')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const importedCode = row.proposal.imported_code;
              return (
              <tr key={`${importedCode}-${row.proposalIndex ?? idx}`} data-testid={`coa-map-row-${importedCode}`} className="border-t">
                <td className="px-2 py-1.5" data-testid={`coa-map-imported-${importedCode}`}>
                  <span className="text-muted-foreground">{idx + 1}.</span>{' '}
                  <span className="font-mono text-muted-foreground">{importedCode}</span>{' '}
                  <span>{row.proposal.imported_name}</span>
                </td>
                <td className="px-2 py-1.5 align-top">
                  <select
                    data-testid={`coa-map-type-${importedCode}`}
                    value={row.account_type}
                    onChange={e => handleTypeChange(idx, e.target.value as CoaAccountType)}
                    className="border rounded px-1 py-0.5 bg-background"
                  >
                    {TYPE_ORDER.map(t => (
                      <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5 text-right align-top">
                  <input
                    data-testid={`coa-map-balance-${importedCode}`}
                    type="number"
                    step="0.01"
                    value={row.balance ?? ''}
                    onChange={e => handleBalanceChange(idx, e.target.value)}
                    className="w-24 border rounded px-1 py-0.5 text-right font-mono"
                  />
                </td>
                <td className="px-2 py-1.5 text-center align-top">
                  <input
                    data-testid={`coa-map-include-${importedCode}`}
                    type="checkbox"
                    checked={row.included}
                    onChange={() => toggleInclude(idx)}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div data-testid="coa-map-tb" className="flex items-center justify-between text-sm font-semibold border-t pt-2">
        <span className="text-muted-foreground">{tr('Trial balance', '試算表', '试算表')}</span>
        <span className="tabular-nums text-base">
          {tr('Net', '淨額', '净额')} <span data-testid="coa-map-tb-net">{tb.net}</span>
          {'   '}
          {tr('Dr', '借', '借')} <span data-testid="coa-map-tb-debit">{tb.debit}</span>
          {' · '}
          {tr('Cr', '貸', '贷')} <span data-testid="coa-map-tb-credit">{tb.credit}</span>
        </span>
      </div>
      {tbWarning}

      <div className="flex justify-end pt-1">
        <button
          data-testid="coa-map-apply"
          onClick={handleApply}
          disabled={rows.length === 0 || rows.some(r => r.included && (r.nameError || r.codeError || r.parentError))}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {tr('Apply Import', '套用導入', '应用导入')}
        </button>
      </div>
      </>
      )}
    </div>
  );
}
